import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './PricingPage.css';

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill="#10b981"/>
    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('annual');
  const [selectedPlan, setSelectedPlan] = useState('Pro');
  const [contacts, setContacts] = useState(500);
  const [users, setUsers] = useState(1);
  const [inbox, setInbox] = useState(false);
  const [dynamicCMS, setDynamicCMS] = useState(false);
  const [doneWithYou, setDoneWithYou] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ platform: true });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileFeaturePlan, setMobileFeaturePlan] = useState('Pro');

  // Contact slider values - starting at 500
  const contactSteps = [500, 1000, 2500, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000];
  const contactIndex = contactSteps.indexOf(contacts) !== -1 ? contactSteps.indexOf(contacts) : 0;

  // Pricing based on plan and billing cycle
  const planPrices = {
    Plus: { monthly: 99, yearly: 83 },
    Pro: { monthly: 149, yearly: 124 },
    Enterprise: { monthly: 299, yearly: 249 }
  };

  const userPrice = 49;
  const currentPlanPrices = planPrices[selectedPlan];
  const baseMonthlyPrice = billingCycle === 'annual' ? currentPlanPrices.yearly : currentPlanPrices.monthly;
  const additionalUsersCost = (users - 1) * userPrice;
  const monthlyPrice = baseMonthlyPrice + additionalUsersCost;
  const yearlyTotal = monthlyPrice * 12;
  const originalYearlyTotal = currentPlanPrices.monthly * 12 + additionalUsersCost * 12;

  const formatContactNumber = (num) => {
    if (num >= 1000000) return 'Up to ' + (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1) + 'M';
    if (num >= 1000) return 'Up to ' + (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + 'K';
    return 'Up to ' + num.toString();
  };

  const plans = [
    {
      name: 'Plus',
      monthlyPrice: 99,
      yearlyPrice: 83,
      tagline: 'CRM and automation essentials for growing teams',
      keyFeatures: [
        'CRM',
        'Marketing Automation',
        'Email + SMS',
        'Websites + Membership',
        'Payment Management',
        'Built-in AI'
      ],
    },
    {
      name: 'Pro',
      monthlyPrice: 149,
      yearlyPrice: 124,
      tagline: 'Data insights and customization for next-level results',
      popular: true,
      keyFeatures: [
        'Everything in Plus, AND',
        'Marketing Tracking + Testing',
        'Advanced Personalization',
        'Deep Customization',
        'User Permissions Management',
        'Partner Programs',
        'Shared Inbox + Webchat'
      ],
    },
    {
      name: 'Enterprise',
      monthlyPrice: 299,
      yearlyPrice: 249,
      tagline: 'Top-grade scalable infrastructure for established teams',
      keyFeatures: [
        'Everything in Pro, AND',
        'SSO',
        'Field-level permissions',
        'Private infrastructure',
        'Enterprise-grade scalability'
      ],
    },
  ];

  // Original feature categories for the Key Features table
  const featureCategories = [
    {
      name: 'Marketing',
      features: [
        { name: 'Email and SMS automation', plus: true, pro: true, enterprise: true },
        { name: 'Split test pages', plus: false, pro: true, enterprise: true },
        { name: 'Partner programs & referral tracking', plus: false, pro: true, enterprise: true },
        { name: 'Lead source and conversion reporting (UTM, Google, FB, LinkedIn pixels)', plus: false, pro: true, enterprise: true },
        { name: 'Custom mailserver domain', plus: '1 included (1 max)', pro: '2 included (max 5)', enterprise: '3 included (no max)' },
      ],
    },
    {
      name: 'Sales CRM',
      features: [
        { name: 'Customizable online database', plus: true, pro: true, enterprise: true },
        { name: 'Smart Pipelines', plus: true, pro: true, enterprise: true },
        { name: 'Self-scheduling and calendar event automation', plus: true, pro: true, enterprise: true },
        { name: 'Task automation', plus: true, pro: true, enterprise: true },
        { name: 'Unified contact history', plus: true, pro: true, enterprise: true },
        { name: 'User roles and permissions', plus: 'Default roles', pro: 'Custom roles', enterprise: 'Field-level permissions' },
        { name: 'API access', plus: true, pro: true, enterprise: 'Increased speed available' },
      ],
    },
    {
      name: 'Payments',
      features: [
        { name: 'Invoicing', plus: true, pro: true, enterprise: true },
        { name: 'Order forms', plus: true, pro: true, enterprise: true },
        { name: 'Manage subscriptions and payment plans', plus: true, pro: true, enterprise: true },
        { name: 'Coupon codes & order bumps', plus: false, pro: true, enterprise: true },
      ],
    },
    {
      name: 'CMS',
      features: [
        { name: 'Drag-and-drop pages', plus: true, pro: true, enterprise: true },
        { name: 'Custom objects', plus: 'Available', pro: 'Available', enterprise: 'Available' },
        { name: 'Customer portals & access control', plus: '1', pro: '3', enterprise: 'Unlimited' },
        { name: 'Conditional content', plus: true, pro: true, enterprise: true },
        { name: 'Database-driven Dynamic CMS', plus: 'Available', pro: 'Available', enterprise: 'Available' },
        { name: 'Custom domains', plus: '1 included (1 max)', pro: '2 included (max 5)', enterprise: '3 included (no max)' },
      ],
    },
    {
      name: 'Support',
      features: [
        { name: 'Unified Multi-channel Inbox', plus: 'Available', pro: 'Available', enterprise: 'Available' },
        { name: 'SMS and Email channels included', plus: true, pro: true, enterprise: true },
        { name: 'AI-powered responses', plus: true, pro: true, enterprise: true },
        { name: 'Automatic routing and escalation', plus: true, pro: true, enterprise: true },
        { name: 'SLA automation', plus: true, pro: true, enterprise: true },
      ],
    },
  ];

  // Accordion feature categories for the full features section at the bottom
  const accordionCategories = [
    {
      id: 'platform',
      name: 'Platform',
      subcategories: [
        {
          name: 'Data management',
          features: [
            { name: 'Customizable online database', plus: true, pro: true, enterprise: true },
            { name: 'Segment and organize', plus: true, pro: true, enterprise: true },
            { name: 'Custom fields per object', plus: '100', pro: '150', enterprise: '200 included, more available' },
            { name: 'Custom domains', plus: '1', pro: '2 included, 3 more available', enterprise: '3 included, more available' },
            { name: 'Additional custom domains', plus: false, pro: '$20/month each', enterprise: '$20/month each' },
            { name: 'Unified contact history', plus: true, pro: true, enterprise: true },
            { name: 'Automation log', plus: true, pro: true, enterprise: true },
            { name: 'Record layout customization', plus: true, pro: true, enterprise: true },
            { name: 'Default roles and user permissions', plus: false, pro: true, enterprise: true },
            { name: 'Custom roles and user permissions', plus: false, pro: false, enterprise: true },
            { name: 'Field-level permissions', plus: false, pro: false, enterprise: true },
            { name: 'Customize navigation by role', plus: false, pro: false, enterprise: true },
            { name: 'Rollup fields', plus: true, pro: true, enterprise: true },
            { name: 'Custom objects', plus: 'Available with Dynamic CMS', pro: 'Available with Dynamic CMS', enterprise: 'Available with Dynamic CMS' },
            { name: 'API', plus: true, pro: true, enterprise: true },
            { name: 'API limits', plus: '180/min standard', pro: '180/min standard', enterprise: 'Faster available' },
            { name: 'AI memory log', plus: 'Available with AI Assistant', pro: 'Available with AI Assistant', enterprise: 'Available with AI Assistant' },
          ],
        },
        {
          name: 'File hosting',
          features: [
            { name: 'Video hosting', plus: true, pro: true, enterprise: true },
            { name: 'Video hosting limits', plus: '200mb per/10gb max', pro: '500mb per/20gb max', enterprise: '1g per/50gb max' },
            { name: 'File/image upload fields', plus: true, pro: true, enterprise: true },
            { name: 'Hosted file storage/download links', plus: true, pro: true, enterprise: true },
          ],
        },
      ],
    },
    {
      id: 'payments',
      name: 'Payments',
      subcategories: [],
    },
    {
      id: 'sales-crm',
      name: 'Sales and CRM',
      subcategories: [],
    },
    {
      id: 'marketing-automation',
      name: 'Marketing and automation',
      subcategories: [],
    },
    {
      id: 'website-pages',
      name: 'Website and pages',
      subcategories: [],
    },
    {
      id: 'reporting',
      name: 'Reporting',
      subcategories: [],
    },
    {
      id: 'support-onboarding',
      name: 'Support and onboarding',
      subcategories: [],
    },
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderFeatureValue = (value) => {
    if (value === true) {
      return <CheckIcon />;
    }
    if (value === false) {
      return <span className="feature-dash">—</span>;
    }
    return <span className="feature-text">{value}</span>;
  };

  return (
    <div className="pricing-page">
      {/* Decorative background elements */}
      <div className="bg-gradient-orb bg-orb-1"></div>
      <div className="bg-gradient-orb bg-orb-2"></div>

      {/* Navigation Bar */}
      <nav className="nav-bar">
        <div className="nav-container">
          <div className="nav-left">
            <a href="/" className="nav-logo">ontraport</a>
            <div className="nav-links">
              <a href="#" className="nav-link">Product</a>
              <a href="#" className="nav-link">Who it's for</a>
              <a href="#" className="nav-link active">Pricing</a>
              <a href="#" className="nav-link">Resources</a>
              <a href="#" className="nav-link">Contact us</a>
            </div>
          </div>
          <div className="nav-right">
            <Link to="/login" className="nav-link">Log in</Link>
            <Link to="/signup" className="nav-cta">Start free trial</Link>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <a href="#" className="mobile-menu-link">Product</a>
            <a href="#" className="mobile-menu-link">Who it's for</a>
            <a href="#" className="mobile-menu-link active">Pricing</a>
            <a href="#" className="mobile-menu-link">Resources</a>
            <a href="#" className="mobile-menu-link">Contact us</a>
            <div className="mobile-menu-divider"></div>
            <Link to="/login" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Log in</Link>
            <Link to="/signup" className="mobile-menu-cta" onClick={() => setMobileMenuOpen(false)}>Start free trial</Link>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pricing-main">
        {/* Hero Section */}
        <section className="pricing-hero">
          <h1 className="pricing-title">Try Ontraport free for 14 days</h1>
          <p className="pricing-subtitle">
            Ontraport brings enterprise-grade CRM and automation software to small and mid-sized companies.
          </p>

          <Link to="/signup" className="hero-cta">Try for free</Link>

          <p className="hero-sales-text">
            Or <a href="#" className="hero-sales-link">book a discovery call</a>.
          </p>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`billing-option ${billingCycle === 'annual' ? 'active' : ''}`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="billing-badge">2 months free</span>
            </button>
          </div>
        </section>

        {/* Pricing Cards + Sidebar Layout */}
        <section className="pricing-layout">
          <div className="pricing-left-content">
            {/* Pricing Cards as Toggles */}
            <div className="pricing-cards">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`pricing-card ${selectedPlan === plan.name ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan(plan.name)}
                >
                  <div className="card-radio">
                    <span className={`radio-dot ${selectedPlan === plan.name ? 'active' : ''}`}></span>
                  </div>
                  <div className="card-content">
                    <div className="card-header">
                      <div className="plan-name-row">
                        <h2 className="plan-name">{plan.name}</h2>
                        {plan.popular && <span className="popular-badge">Most popular</span>}
                      </div>
                      <p className="plan-tagline">{plan.tagline}</p>
                    </div>
                    <div className="card-pricing">
                      <span className="price-currency">$</span>
                      <span className="price-amount">{billingCycle === 'annual' ? plan.yearlyPrice : plan.monthlyPrice}</span>
                      <span className="price-period"> per month</span>
                    </div>
                    <div className="key-features">
                      <h4>Key features include</h4>
                      <ul>
                        {plan.keyFeatures.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Feature Comparison Table */}
            <div className="features-section">
              <h2 className="features-title">Key Features</h2>
              
              <div className="features-table-container">
                <table className="features-table">
                  <thead>
                    <tr>
                      <th className="feature-name-header"></th>
                      <th className="plan-header">Plus</th>
                      <th className="plan-header">Pro</th>
                      <th className="plan-header">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureCategories.map((category) => (
                      <React.Fragment key={category.name}>
                        <tr className="category-row">
                          <td className="category-name" colSpan="4">{category.name}</td>
                        </tr>
                        {category.features.map((feature) => (
                          <tr key={feature.name} className="feature-row">
                            <td className="feature-name">{feature.name}</td>
                            <td className="feature-value">{renderFeatureValue(feature.plus)}</td>
                            <td className="feature-value">{renderFeatureValue(feature.pro)}</td>
                            <td className="feature-value">{renderFeatureValue(feature.enterprise)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Inbox Add-on Section */}
            <section className="addon-detail-section" id="inbox-section">
              <div className="addon-detail-header">
                <h2 className="addon-detail-title">Inbox</h2>
                <p className="addon-detail-description no-wrap">
                  Manage all your customer support needs in one place. <a href="#" className="addon-detail-link">Learn more about Inbox.</a>
                </p>
                <p className="addon-detail-note">Your Inbox add-on price will depend on the Ontraport plan you select.</p>
                <p className="addon-detail-asterisk">Overage fees may apply</p>
                <a href="#" className="watch-video-link">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#1285ff"/>
                    <path d="M8 6.5L14 10L8 13.5V6.5Z" fill="white"/>
                  </svg>
                  Watch the video
                </a>
              </div>

              <div className="addon-table-container">
                <table className="addon-comparison-table inbox-table">
                  <thead>
                    <tr>
                      <th className="addon-feature-header"></th>
                      <th className="addon-plan-header">
                        <span className="addon-plan-name">Plus</span>
                        <span className="addon-plan-price">$28/user per month</span>
                      </th>
                      <th className="addon-plan-header">
                        <span className="addon-plan-name">Pro</span>
                        <span className="addon-plan-price">$58/user per month</span>
                      </th>
                      <th className="addon-plan-header">
                        <span className="addon-plan-name">Enterprise</span>
                        <span className="addon-plan-price">$83/user per month</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Channels included</td>
                      <td className="addon-feature-value"><span className="feature-text-small">Email<br/>SMS*</span></td>
                      <td className="addon-feature-value"><span className="feature-text-small">Email<br/>SMS*</span></td>
                      <td className="addon-feature-value"><span className="feature-text-small">Email<br/>SMS*</span></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Inbox automation</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Default inbox routing</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Start conversations via forms</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="addon-table-footnote">*SMS requires an SMS plan</p>
            </section>

            {/* Dynamic CMS Add-on Section */}
            <section className="addon-detail-section" id="dynamic-cms-section">
              <div className="addon-detail-header">
                <h2 className="addon-detail-title">Dynamic CMS</h2>
                <p className="addon-detail-description no-wrap">
                  Display unique, personalized content for your contacts. Learn more about Dynamic CMS <a href="#" className="addon-detail-link">here</a> and <a href="#" className="addon-detail-link">here</a>.
                </p>
                <p className="addon-detail-asterisk">Overage fees may apply</p>
                <a href="#" className="watch-video-link">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#1285ff"/>
                    <path d="M8 6.5L14 10L8 13.5V6.5Z" fill="white"/>
                  </svg>
                  Watch the video
                </a>
              </div>

              <div className="addon-table-container">
                <table className="addon-comparison-table">
                  <thead>
                    <tr>
                      <th className="addon-feature-header"></th>
                      <th className="addon-plan-header">
                        <span className="addon-plan-name">Starter</span>
                        <span className="addon-plan-price">$41/month</span>
                        <label className="addon-add-toggle">
                          <span>Add</span>
                          <input type="checkbox" />
                          <span className="toggle-track"></span>
                        </label>
                      </th>
                      <th className="addon-plan-header">
                        <span className="addon-plan-name">Builder</span>
                        <span className="addon-plan-price">$83/month</span>
                        <label className="addon-add-toggle">
                          <span>Add</span>
                          <input type="checkbox" />
                          <span className="toggle-track"></span>
                        </label>
                      </th>
                      <th className="addon-plan-header">
                        <span className="addon-plan-name">Architect</span>
                        <span className="addon-plan-price">$124/month</span>
                        <label className="addon-add-toggle">
                          <span>Add</span>
                          <input type="checkbox" />
                          <span className="toggle-track"></span>
                        </label>
                      </th>
                      <th className="addon-plan-header">
                        <span className="addon-plan-name">Visionary</span>
                        <span className="addon-plan-price">Custom pricing</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Custom objects included</td>
                      <td className="addon-feature-value">2</td>
                      <td className="addon-feature-value">5</td>
                      <td className="addon-feature-value">8</td>
                      <td className="addon-feature-value visionary-contact" rowSpan="5"><a href="#" className="contact-link">Contact us</a> for custom pricing up to 5 million records and unlimited views.</td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Additional custom objects</td>
                      <td className="addon-feature-value">$20/month</td>
                      <td className="addon-feature-value">$20/month</td>
                      <td className="addon-feature-value">$20/month</td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">*Records stored per custom object</td>
                      <td className="addon-feature-value">2,000</td>
                      <td className="addon-feature-value">10,000</td>
                      <td className="addon-feature-value">25,000</td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">*Total dynamic records displayed/month</td>
                      <td className="addon-feature-value">10,000</td>
                      <td className="addon-feature-value">50,000</td>
                      <td className="addon-feature-value">200,000</td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Rollup fields</td>
                      <td className="addon-feature-value">50</td>
                      <td className="addon-feature-value">100</td>
                      <td className="addon-feature-value">200</td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">High-performance caching server</td>
                      <td className="addon-feature-value">$49/month</td>
                      <td className="addon-feature-value">$99/month</td>
                      <td className="addon-feature-value">$149/month</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Custom objects</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">App templates for blogs, forums, courses and more</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Database-driven page templates</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Dynamic page hosting</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Dynamic blocks</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Grid layout</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Search</td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Filter and sort</td>
                      <td className="addon-feature-value"><span className="feature-dash">—</span></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                    <tr className="addon-feature-row">
                      <td className="addon-feature-name">Dynamic list HTML tags</td>
                      <td className="addon-feature-value"><span className="feature-dash">—</span></td>
                      <td className="addon-feature-value"><span className="feature-dash">—</span></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                      <td className="addon-feature-value"><CheckIcon /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="addon-table-footnotes">
                <p>*Exceeding these limits will auto-upgrade Starter and Builder plans to the next highest plan. Architect and Visionary plans will incur overage fees:</p>
                <ul>
                  <li>$50/mo for an additional 200k records displayed</li>
                  <li>$50/mo for an additional 5k stored records</li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="pricing-sidebar">
            <div className="sidebar-content">
              {/* Contacts Slider */}
              <div className="sidebar-section">
                <div className="sidebar-row">
                  <label className="sidebar-label">How many contacts?</label>
                  <span className="sidebar-value">{formatContactNumber(contacts)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={contactSteps.length - 1}
                  value={contactIndex}
                  onChange={(e) => setContacts(contactSteps[parseInt(e.target.value)])}
                  className="sidebar-slider"
                  style={{ '--slider-progress': `${(contactIndex / (contactSteps.length - 1)) * 100}%` }}
                />
                <div className="slider-labels">
                  <span>500</span>
                  <span>5,000,000</span>
                </div>
              </div>

              {/* Users Slider */}
              <div className="sidebar-section">
                <div className="sidebar-row">
                  <div>
                    <label className="sidebar-label">How many users?</label>
                    <span className="sidebar-sublabel">${userPrice} per month per user</span>
                  </div>
                  <span className="sidebar-value">{users}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={users}
                  onChange={(e) => setUsers(parseInt(e.target.value))}
                  className="sidebar-slider"
                  style={{ '--slider-progress': `${((users - 1) / 19) * 100}%` }}
                />
                <div className="slider-labels">
                  <span>1</span>
                  <span>Unlimited</span>
                </div>
              </div>

              {/* Inbox Add-on */}
              <div className="sidebar-section addon-section">
                <div className="sidebar-row">
                  <div>
                    <label className="sidebar-label">Inbox</label>
                    <a href="#inbox-section" className="sidebar-link">See details</a>
                  </div>
                  <label className="toggle-switch">
                    <span className="toggle-label">Add</span>
                    <input
                      type="checkbox"
                      checked={inbox}
                      onChange={(e) => setInbox(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Dynamic CMS Add-on */}
              <div className="sidebar-section addon-section">
                <div className="sidebar-row">
                  <div>
                    <label className="sidebar-label">Dynamic CMS</label>
                    <a href="#dynamic-cms-section" className="sidebar-link">See details</a>
                  </div>
                  <label className="toggle-switch">
                    <span className="toggle-label">Add</span>
                    <input
                      type="checkbox"
                      checked={dynamicCMS}
                      onChange={(e) => setDynamicCMS(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Done-With-You Setup */}
              <div className="sidebar-section addon-section">
                <div className="sidebar-row">
                  <div>
                    <label className="sidebar-label">"Done-with-you"<br />Setup and Training</label>
                  </div>
                  <label className="toggle-switch">
                    <span className="toggle-label">Add</span>
                    <input
                      type="checkbox"
                      checked={doneWithYou}
                      onChange={(e) => setDoneWithYou(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="addon-price">$897</div>
                <p className="addon-note">One time fee, 30-day money back guarantee. <a href="#" className="sidebar-link">See details</a></p>
              </div>

              {/* Price Display */}
              <div className="sidebar-section price-section">
                <div className="total-price">
                  <span className="price-dollar">$</span>
                  <span className="price-number">{monthlyPrice}</span>
                  <span className="price-term"> per month</span>
                </div>
                {billingCycle === 'annual' && (
                  <p className="billed-yearly">
                    <span className="price-strikethrough">${originalYearlyTotal}</span> ${yearlyTotal} billed yearly
                  </p>
                )}
              </div>

              {/* CTA Button */}
              <Link to="/signup" className="sidebar-cta">
                Start free trial
              </Link>

              {/* Fine Print */}
              <p className="sidebar-fine-print">
                Billed after your 14-day free trial<br />
                30-day money back guarantee
              </p>
            </div>
          </aside>
        </section>

        {/* Done-with-you Setup Promo Section - Full Width */}
        <section className="setup-promo-section">
          <div className="setup-promo-image">
            <img 
              src="https://optassets.ontraport.com/opt_assets/images/done-with-you-bg.png" 
              alt="Ontraport specialist ready to help" 
            />
          </div>
          <div className="setup-promo-content">
            <div className="setup-promo-badge">57% OFF</div>
            <h2 className="setup-promo-title">
              The fastest and easiest way to get started on the right track
            </h2>
            <h3 className="setup-promo-subtitle">
              Add the Done-with-you Setup and Training Package to your plan
            </h3>
            <p className="setup-promo-description">
              With this personalized setup package, you'll work with a senior rep to clarify your goals and make a plan to achieve them in Ontraport. Then your rep will work with you live, side-by-side via screen share to get you all set up.
            </p>
            <div className="setup-promo-actions">
              <button className="btn-add-to-plan">Add to plan +</button>
              <a href="#" className="learn-more-link">Learn more</a>
            </div>
          </div>
        </section>

        {/* Full Features Accordion Section */}
        <section className="full-features-section">
          <h2 className="section-title">Compare features</h2>
          
          {/* Mobile Plan Selector */}
          <div className="mobile-plan-selector">
            <button 
              className={`mobile-plan-btn ${mobileFeaturePlan === 'Plus' ? 'active' : ''}`}
              onClick={() => setMobileFeaturePlan('Plus')}
            >
              Plus
            </button>
            <button 
              className={`mobile-plan-btn ${mobileFeaturePlan === 'Pro' ? 'active' : ''}`}
              onClick={() => setMobileFeaturePlan('Pro')}
            >
              Pro
            </button>
            <button 
              className={`mobile-plan-btn ${mobileFeaturePlan === 'Enterprise' ? 'active' : ''}`}
              onClick={() => setMobileFeaturePlan('Enterprise')}
            >
              Enterprise
            </button>
          </div>

          <div className="features-accordion-section" style={{ border: '1px solid #e8e9ea', borderRadius: '24px', background: '#ffffff', overflow: 'clip' }}>
            {/* Sticky Header */}
            <table className="accordion-header-table">
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="accordion-header-spacer"></th>
                  <th className="accordion-plan-header">Plus</th>
                  <th className="accordion-plan-header">Pro</th>
                  <th className="accordion-plan-header">Enterprise</th>
                </tr>
              </thead>
            </table>

            {/* Accordion Categories */}
            <div className="accordion-categories">
              {accordionCategories.map((category) => (
                <div key={category.id} className={`accordion-category ${expandedSections[category.id] ? 'expanded' : ''}`}>
                  <button 
                    className="accordion-category-header"
                    onClick={() => toggleSection(category.id)}
                    aria-expanded={expandedSections[category.id]}
                  >
                    <span className="accordion-chevron">
                      <ChevronDownIcon />
                    </span>
                    <span className="accordion-category-name">{category.name}</span>
                    <span className="accordion-see-details">
                      {expandedSections[category.id] ? 'Hide details' : 'See details'}
                    </span>
                  </button>
                  
                  {expandedSections[category.id] && (
                    <div className="accordion-category-content">
                      {category.subcategories.map((subcategory, subIndex) => (
                        <div key={subIndex} className="accordion-subcategory">
                          <table className="accordion-features-table">
                            <colgroup>
                              <col style={{ width: '40%' }} />
                              <col style={{ width: '20%' }} />
                              <col style={{ width: '20%' }} />
                              <col style={{ width: '20%' }} />
                            </colgroup>
                            <tbody>
                              {subcategory.name && (
                                <tr className="accordion-subcategory-row">
                                  <td colSpan="4" className="accordion-subcategory-title">{subcategory.name}</td>
                                </tr>
                              )}
                              {subcategory.features.map((feature, featureIndex) => (
                                <tr key={featureIndex} className="accordion-feature-row">
                                  <td className="accordion-feature-name">{feature.name}</td>
                                  <td className={`accordion-feature-value plan-plus ${mobileFeaturePlan === 'Plus' ? 'mobile-visible' : ''}`}>{renderFeatureValue(feature.plus)}</td>
                                  <td className={`accordion-feature-value plan-pro ${mobileFeaturePlan === 'Pro' ? 'mobile-visible' : ''}`}>{renderFeatureValue(feature.pro)}</td>
                                  <td className={`accordion-feature-value plan-enterprise ${mobileFeaturePlan === 'Enterprise' ? 'mobile-visible' : ''}`}>{renderFeatureValue(feature.enterprise)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Talk to Sales Banner */}
            <div className="talk-to-sales-banner">
              <div className="talk-to-sales-avatars">
                <img src="https://i.ontraport.com/3.0684142c8a3a9b6805368f355762ae90.PNG" alt="Sales team" className="sales-avatars-img" />
              </div>
              <div className="talk-to-sales-content">
                <h3 className="talk-to-sales-title">Don't see what you're looking for? Let's talk.</h3>
              </div>
              <a href="#" className="talk-to-sales-link">Talk to sales</a>
            </div>
          </div>

          {/* Testimonial Section */}
          <div className="testimonial-section">
            <div className="testimonial-image">
              <img src="https://i.ontraport.com/3.6883d9b71bff6084521ea9e576514bbf.PNG" alt="Matthew Canning" className="testimonial-photo" />
            </div>
            <div className="testimonial-content">
              <blockquote className="testimonial-quote">
                "The onboarding calls are a fantastic value-add for new users and I'm consistently blown away by the support team's professionalism and friendliness. I feel like I truly have a team working with me who is invested in my success."
              </blockquote>
              <p className="testimonial-author">— Matthew Canning, Foundations of Execution</p>
            </div>
          </div>

          {/* Getting Started Section */}
          <div className="getting-started-section">
            <h2 className="getting-started-title">It's easy to get started with Ontraport</h2>
            <p className="getting-started-subtitle">
              When you sign up for Ontraport, you're getting more than software — you're joining a supportive community.
            </p>
            <div className="getting-started-cards">
              <div className="getting-started-card">
                <img src="https://i.ontraport.com/3.24081c91725985787dddf0a67b166847.PNG" alt="Templates and resources" className="getting-started-icon" />
                <h3 className="getting-started-card-title">Templates and resources</h3>
                <p className="getting-started-card-text">Meet your goals faster with templates, step-by-step walkthroughs, instructional videos and more.</p>
              </div>
              <div className="getting-started-card">
                <img src="https://i.ontraport.com/3.c8e4fb4621526555837812a7bdf3ecad.PNG" alt="Amazing support" className="getting-started-icon" />
                <h3 className="getting-started-card-title">Amazing support</h3>
                <p className="getting-started-card-text">Reach our support team via chat, email, or screen share seven days a week — they're known for being extra helpful and fast.</p>
              </div>
              <div className="getting-started-card">
                <img src="https://i.ontraport.com/3.69579862028fcfa28ee211f4064236e1.PNG" alt="An inspiring community" className="getting-started-icon" />
                <h3 className="getting-started-card-title">An inspiring community</h3>
                <p className="getting-started-card-text">Join our active group of entrepreneurs from around the world. Ask questions and see how businesses like yours use Ontraport.</p>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* CTA Banner */}
      <section className="cta-banner-section">
        {/* Decorative dots - left side */}
        <div className="cta-dots cta-dots-left">
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </div>
        
        <div className="cta-banner" style={{ border: '1px solid #e8e9ea', borderRadius: '24px', background: '#f8f7f5', overflow: 'hidden' }}>
          <h2 className="cta-banner-title">Start growing your business with Ontraport</h2>
          <p className="cta-banner-subtitle">All plans start with a 14-day free trial. No credit card required to sign up.</p>
          <Link to="/signup" className="cta-banner-button">Start free trial</Link>
        </div>
        
        {/* Decorative dots - right side */}
        <div className="cta-dots cta-dots-right">
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-content">
        <h2 className="faq-title">Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3 className="faq-question">How does the 14-day free trial work?</h3>
            <p className="faq-answer">All accounts start with a <a href="#">14-day free trial</a>. You can upgrade to your chosen plan inside the Ontraport platform any time before your trial ends. If you choose not to upgrade you won't need to do anything to cancel, and you won't be charged (in fact, we won't even require your credit card information until you upgrade).</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">What if I want to upgrade to a different plan later?</h3>
            <p className="faq-answer">Can do! You can easily upgrade your account from within the platform any time.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">Do I have to commit to a long-term contract?</h3>
            <p className="faq-answer">Nope! You can choose whether you'd like to pay monthly or annually. If you choose an annual subscription, you're committing for the year — with two months free!</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">What if I want to cancel my account later?</h3>
            <p className="faq-answer">Though we'd love to support your growing business, we understand! You can simply cancel from within your account at any time. If you cancel within the first 30 days after signing up, just reach out to us, and we'll refund your payment completely.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">Is there support for getting my account set up?</h3>
            <p className="faq-answer">Yes, you can add the Done-with-you Setup and Training Package to get a personalized plan for achieving your goals in Ontraport and one-on-one setup support. You can also access <a href="#">Ontraport University</a>, our free 150+ video library filled with step-by-step instructions and strategic advice, including an entire Getting Started course to get you on the right track.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">What if I'm not sure which Ontraport plan is right for me?</h3>
            <p className="faq-answer">Talk with us! Our business solutions team can discuss your business goals with you and recommend the plan that's best for your needs. Schedule a call <a href="#">here</a>.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">I have more questions before I decide. Who do I ask?</h3>
            <p className="faq-answer">You can reach our business solutions team by scheduling a call <a href="#">here</a>. They'll answer your questions, show you around the platform, and assess your needs so you'll know whether Ontraport is the right choice for your business.</p>
          </div>
        </div>
        </div>
      </section>

      {/* Full Footer */}
      <footer className="full-footer">
        <div className="footer-main">
          <div className="footer-logo">ontraport</div>
          
          <div className="footer-columns">
            <div className="footer-column">
              <h4 className="footer-column-heading">Features</h4>
              <div className="footer-sub-columns">
                <div className="footer-sub-column">
                  <h5 className="footer-sub-heading">Sales + CRM</h5>
                  <ul>
                    <li><a href="#">Segment + organize</a></li>
                    <li><a href="#">Smart pipelines</a></li>
                    <li><a href="#">Self-scheduling + calendar</a></li>
                    <li><a href="#">Task automation</a></li>
                    <li><a href="#">Lead scoring + routing</a></li>
                    <li><a href="#">Unified contact history</a></li>
                    <li><a href="#">Mobile app</a></li>
                    <li><a href="#">Trends</a></li>
                  </ul>
                </div>
                <div className="footer-sub-column">
                  <h5 className="footer-sub-heading">Payments</h5>
                  <ul>
                    <li><a href="#">Order forms</a></li>
                    <li><a href="#">Offline transactions</a></li>
                    <li><a href="#">Complex offers</a></li>
                    <li><a href="#">Upsells + order bumps</a></li>
                    <li><a href="#">Decline recovery</a></li>
                    <li><a href="#">Customer center</a></li>
                    <li><a href="#">Partner programs + referral tracking</a></li>
                  </ul>
                </div>
                <div className="footer-sub-column">
                  <h5 className="footer-sub-heading">Marketing automation</h5>
                  <ul>
                    <li><a href="#">Campaign automation</a></li>
                    <li><a href="#">Email + SMS messaging</a></li>
                    <li><a href="#">Email deliverability</a></li>
                    <li><a href="#">Meta custom audiences</a></li>
                    <li><a href="#">Tracking + attribution</a></li>
                  </ul>
                </div>
                <div className="footer-sub-column">
                  <h5 className="footer-sub-heading">CMS</h5>
                  <ul>
                    <li><a href="#">Drag-and-drop pages</a></li>
                    <li><a href="#">Dynamic CMS</a></li>
                    <li><a href="#">Custom objects</a></li>
                    <li><a href="#">Access control + membership</a></li>
                    <li><a href="#">Form builder</a></li>
                    <li><a href="#">Conditional content</a></li>
                    <li><a href="#">WordPress integrations</a></li>
                    <li><a href="#">API</a></li>
                  </ul>
                </div>
                <div className="footer-sub-column">
                  <h5 className="footer-sub-heading">Support</h5>
                  <ul>
                    <li><a href="#">Shared inbox</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom-columns">
            <div className="footer-bottom-column">
              <h5 className="footer-sub-heading">Company</h5>
              <ul>
                <li><a href="#">About us</a></li>
                <li><a href="#">Pricing</a></li>
                <li><a href="#">Press</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#">Service status</a></li>
                <li><a href="#">Legal</a></li>
              </ul>
            </div>
            <div className="footer-bottom-column">
              <h5 className="footer-sub-heading">Resources</h5>
              <ul>
                <li><a href="#">What's new</a></li>
                <li><a href="#">Support Center</a></li>
                <li><a href="#">Step-by-step templates</a></li>
                <li><a href="#">In-depth guides</a></li>
                <li><a href="#">Setup wizard library</a></li>
              </ul>
            </div>
            <div className="footer-bottom-column">
              <h5 className="footer-sub-heading">Live events</h5>
              <ul>
                <li><a href="#">Bootcamp course</a></li>
                <li><a href="#">Certification course</a></li>
                <li><a href="#">Ontrapalooza</a></li>
              </ul>
            </div>
            <div className="footer-bottom-column">
              <h5 className="footer-sub-heading">Access</h5>
              <ul>
                <li><a href="#">Partner login</a></li>
                <li><a href="#">Expert login</a></li>
                <li><a href="#">Ontraport login</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="footer-legal">
          <span className="footer-copyright">© Ontraport 2025</span>
          <div className="footer-legal-links">
            <a href="#">Terms & Conditions</a>
            <span className="footer-legal-divider">|</span>
            <a href="#">Privacy Policy</a>
          </div>
          <span className="footer-pci">
            <ShieldIcon />
            PCI DSS, Level 1
          </span>
        </div>

        {/* Prototype Links Section */}
        <div className="prototype-links">
          <h4 className="prototype-links-title">Prototype Pages</h4>
          <div className="prototype-links-list">
            <Link to="/sms-plans" className="prototype-link">SMS Plans</Link>
            <Link to="/dynamic-cms" className="prototype-link">Dynamic CMS</Link>
            <Link to="/inbox" className="prototype-link">Inbox</Link>
            <Link to="/verify-identity" className="prototype-link">Verify Identity</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PricingPage;
